import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    TextField,
    Typography
} from "@mui/material/";
import actions from "../../Store/actions";
import { useAuthenticated } from "../hooks";
import { strings } from "../i18n";
import { logToSentry } from "../../config/Sentry";
import { env } from "../lib";

const initialState = { confirmPassword: "", password: "" };

type Props = {
    onClose: Function,
    open: boolean
};

/**
 * Set Password Dialog Component
 */
export default ({ onClose, open }: Props) => {
    const { identityId } = useSelector((state) => state.auth);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const dispatch = useDispatch();
    const [errors, setErrors] = React.useState("");
    const [loading, setLoading] = React.useState(false);
    const [password, setPassword] = React.useState({ ...initialState });

    const handleChange = (name: string) => (event: Event) => {
        setPassword({ ...password, [name]: event.target.value });
        setErrors("");
    };

    const onBlur = React.useCallback(() => {
        let next = "";
        if (password.confirmPassword.length && password.password.length) {
            if (password.confirmPassword !== password.password) {
                next = strings("errors.passwordsDoNotMatch");
            }
        }
        setErrors(next);
    }, [errors, password]);

    const onDialogClose = () => {
        setDialogOpen(false);
        onClose && onClose();
    };

    const onSubmit = async () => {
        try {
            setLoading(true);
            await dispatch(
                actions.auth.create.setPasswordPatchRequest(
                    identityId,
                    password.password
                )
            );
            dispatch(
                actions.toast.create.success("messages.setPasswordSuccess")
            );
            onDialogClose();
        } catch (error) {
            console.error(error);
            logToSentry("On set password:", { error });
        }
    };

    const onValidate = () => {
        if (!(password.confirmPassword.length && password.password.length)) {
            return false;
        }
        if (password.confirmPassword !== password.password) {
            return false;
        }
        return true;
    };

    React.useEffect(() => {
        open && setDialogOpen(true);
        setPassword({ ...initialState });
    }, [open]);

    return (
        <Dialog open={dialogOpen} onClose={onDialogClose}>
            <DialogTitle>{strings("changePassword")}</DialogTitle>
            <DialogContent>
                <FormControl fullWidth margin="normal">
                    <TextField
                        error={Boolean(errors)}
                        InputLabelProps={{ shrink: Boolean(password.password) }}
                        label={strings("newPassword")}
                        onBlur={onBlur}
                        onChange={handleChange("password")}
                        type="password"
                        value={password.password}
                    />
                </FormControl>
                <FormControl fullWidth margin="normal">
                    <TextField
                        error={Boolean(errors)}
                        InputLabelProps={{
                            shrink: Boolean(password.confirmPassword)
                        }}
                        helperText={errors}
                        label={strings("confirmPassword")}
                        onBlur={onBlur}
                        onChange={handleChange("confirmPassword")}
                        type="password"
                        value={password.confirmPassword}
                    />
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button disabled={loading || !onValidate()} onClick={onSubmit}>
                    {strings("save")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
